import * as React from 'react';
import Modal from '@mui/joy/Modal';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import DialogActions from '@mui/joy/DialogActions';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import {
  ModalClose,
  ModalDialog,
  Stack,
  FormHelperText,
  Typography,
  Select,
  Option,
  Alert,
  Card,
  CardContent,
  Grid,
  Skeleton,
  IconButton,
  Tabs,
  TabList,
  Tab,
  Box,
  Chip,
  Switch,
} from '@mui/joy';
import { ArrowLeftIcon, ArrowRightIcon, XIcon } from 'lucide-react';
import { useExperimentInfo } from 'renderer/lib/ExperimentInfoContext';
import * as chatAPI from 'renderer/lib/transformerlab-api-sdk';
import { useSWRWithAuth as useSWR } from 'renderer/lib/authContext';
import { fetcher } from 'renderer/lib/transformerlab-api-sdk';
import { useNotification } from 'renderer/components/Shared/NotificationSystem';
import { generateFriendlyName } from 'renderer/lib/utils';
import {
  getPreferredProviderId,
  isProviderCompatibleWithAccelerators,
} from './providerCompatibility';
import ModelNameInput from 'renderer/components/Shared/ModelNameInput';
import {
  getModelHistoryKey,
  saveModelToHistory,
} from 'renderer/components/Shared/modelHistory';

type ProviderOption = {
  id: string;
  name: string;
  type?: string;
  config?: any;
};

type ConfigField = {
  field_name: string;
  env_var: string;
  field_type: 'str' | 'integer' | 'model';
  required?: boolean;
  placeholder?: string;
  help_text?: string;
  password?: boolean;
};

type InteractiveTemplate = {
  id: string;
  interactive_type: string;
  name: string;
  description: string;
  env_parameters?: ConfigField[];
  icon?: string;
  supported_accelerators?: string;
  /** When true, task is hidden when a local compute provider is selected. */
  remoteOnly?: boolean;
  /** Optional per-environment (local/remote) and per-accelerator command variants; backend resolves at launch. */
  commands?: Record<
    string,
    Record<string, string | { setup?: string; command: string }>
  >;
};

type ImportedTask = {
  id: string;
  name: string;
  config: string | object;
  interactive_type?: string;
};

const EMPTY_IMPORTED_TASKS: ImportedTask[] = [];

type NewInteractiveTaskModalProps = {
  open: boolean;
  onClose: () => void;
  submitError?: string | null;
  onClearSubmitError?: () => void;
  onSubmit: (
    data: {
      title: string;
      cpus?: string;
      memory?: string;
      accelerators?: string;
      interactive_type: 'vscode' | 'jupyter' | 'vllm' | 'ssh' | 'ollama';
      template_id: string;
      provider_id?: string;
      env_parameters?: Record<string, string>;
      local?: boolean;
    },
    shouldLaunch?: boolean,
  ) => void;
  isSubmitting?: boolean;
  providers: ProviderOption[];
  isProvidersLoading?: boolean;
  importedTasks: ImportedTask[];
  onDeleteTask: (taskId: string) => void;
  onQueueTask: (task: ImportedTask) => void;
  onRefreshTasks: () => void;
};

export default function NewInteractiveTaskModal({
  open,
  onClose,
  submitError = null,
  onClearSubmitError,
  onSubmit,
  isSubmitting = false,
  providers,
  isProvidersLoading = false,
  importedTasks = EMPTY_IMPORTED_TASKS,
  onDeleteTask,
  onQueueTask,
  onRefreshTasks,
}: NewInteractiveTaskModalProps) {
  const { experimentInfo } = useExperimentInfo();
  const [step, setStep] = React.useState<'provider' | 'gallery' | 'config'>(
    'provider',
  );
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<InteractiveTemplate | null>(null);
  const [title, setTitle] = React.useState(() => generateFriendlyName());
  const [cpus, setCpus] = React.useState('');
  const [memory, setMemory] = React.useState('');
  const [accelerators, setAccelerators] = React.useState('');
  const [selectedProviderId, setSelectedProviderId] = React.useState('');
  const [configFieldValues, setConfigFieldValues] = React.useState<
    Record<string, string>
  >({});
  const [modelRegistryToggles, setModelRegistryToggles] = React.useState<
    Record<string, boolean>
  >({});
  const [activeGalleryTab, setActiveGalleryTab] = React.useState<
    'interactive' | 'team-interactive'
  >('interactive');
  const [importingTeamTaskId, setImportingTeamTaskId] = React.useState<
    string | number | null
  >(null);
  const { addNotification } = useNotification();

  const loadingMessages = React.useMemo(
    () => [
      'Contacting compute provider…',
      'Reserving resources…',
      'Preparing environment…',
      'Submitting job configuration…',
      'Waiting for job ID…',
    ],
    [],
  );
  const [loadingMessageIndex, setLoadingMessageIndex] = React.useState(0);

  React.useEffect(() => {
    if (!open || !isSubmitting || loadingMessages.length === 0) {
      return;
    }

    setLoadingMessageIndex(0);

    const interval = window.setInterval(() => {
      setLoadingMessageIndex((prev) => {
        const lastIndex = loadingMessages.length - 1;
        if (prev >= lastIndex) {
          return lastIndex;
        }
        return prev + 1;
      });
    }, 1500);

    return () => {
      window.clearInterval(interval);
    };
  }, [open, isSubmitting, loadingMessages]);

  const isProviderCompatible = React.useCallback(
    (provider: any, taskSupportedAccelerators: string | string[] | undefined) =>
      isProviderCompatibleWithAccelerators(provider, taskSupportedAccelerators),
    [],
  );

  // Fetch interactive gallery
  const { data: galleryData, isLoading: galleryIsLoading } = useSWR(
    experimentInfo?.id && open
      ? chatAPI.Endpoints.Task.InteractiveGallery(experimentInfo.id)
      : null,
    fetcher,
  );

  // Fetch team gallery
  const { data: teamGalleryData, isLoading: teamGalleryIsLoading } = useSWR(
    experimentInfo?.id && open
      ? chatAPI.Endpoints.Task.TeamGallery(experimentInfo.id)
      : null,
    fetcher,
  );

  const gallery = React.useMemo(() => {
    if (galleryData?.status === 'success' && Array.isArray(galleryData.data)) {
      return galleryData.data as InteractiveTemplate[];
    }
    return [];
  }, [galleryData]);

  const teamGallery = React.useMemo(() => {
    if (
      teamGalleryData?.status === 'success' &&
      Array.isArray(teamGalleryData.data)
    ) {
      return teamGalleryData.data as any[];
    }
    return [];
  }, [teamGalleryData]);

  const teamInteractiveGallery = React.useMemo(() => {
    return teamGallery.filter(
      (entry: any) =>
        entry?.subtype === 'interactive' ||
        entry?.config?.subtype === 'interactive',
    );
  }, [teamGallery]);

  const { data: installedModelsData } = useSWR(
    open && step === 'config' ? chatAPI.Endpoints.Models.LocalList() : null,
    fetcher,
  );
  const { data: registryModelsData } = useSWR(
    open && step === 'config'
      ? chatAPI.Endpoints.Models.RegistryVersionList()
      : null,
    fetcher,
  );

  const installedModels = React.useMemo(() => {
    if (!installedModelsData || !Array.isArray(installedModelsData)) return [];
    return installedModelsData as Array<{ model_id: string; name?: string }>;
  }, [installedModelsData]);

  const registryModels = React.useMemo(() => {
    if (!registryModelsData || !Array.isArray(registryModelsData)) return [];
    return registryModelsData as Array<{ model_id: string; name?: string }>;
  }, [registryModelsData]);

  React.useEffect(() => {
    if (!open) {
      setStep('provider');
      setSelectedTemplate(null);
      setTitle(generateFriendlyName());
      setCpus('');
      setMemory('');
      setAccelerators('');
      setConfigFieldValues({});
      setSelectedProviderId('');
      setActiveGalleryTab('interactive');
      setModelRegistryToggles({});
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    if (!providers.length) {
      setSelectedProviderId('');
      return;
    }
    if (!selectedProviderId) {
      // Auto-select default provider (or first available if none is marked default).
      setSelectedProviderId(getPreferredProviderId(providers));
      return;
    }
    if (!providers.find((p) => p.id === selectedProviderId)) {
      // If the previously selected provider is no longer available,
      // fall back to default provider, then first available provider.
      setSelectedProviderId(getPreferredProviderId(providers));
    }
  }, [open, providers, selectedProviderId]);

  const selectedProvider = React.useMemo(
    () => providers.find((p) => p.id === selectedProviderId),
    [providers, selectedProviderId],
  );

  /** True when the chosen compute provider is the local one; ngrok is not required then. */
  const isLocalProvider = selectedProvider?.type === 'local';

  const handleTemplateSelect = (template: InteractiveTemplate) => {
    if (isSubmitting) {
      return;
    }
    setSelectedTemplate(template);
    setStep('config');
    // Initialize config field values
    const initialValues: Record<string, string> = {};
    template.env_parameters?.forEach((field) => {
      if (field.field_type === 'integer' && field.env_var === 'TP_SIZE') {
        initialValues[field.env_var] = '1';
      }
      if (field.env_var === 'NGROK_AUTH_TOKEN' && !isLocalProvider) {
        initialValues[field.env_var] = '{{secret._NGROK_AUTH_TOKEN}}';
      }
    });
    setConfigFieldValues(initialValues);
  };

  // Keep NGROK_AUTH_TOKEN aligned with local/remote behavior:
  // - For remote (non-local) sessions, default to {{secret._NGROK_AUTH_TOKEN}} if empty.
  // - For local/direct sessions, drop NGROK_AUTH_TOKEN so secret is not required.
  React.useEffect(() => {
    if (!selectedTemplate) return;

    const hasNgrokField = selectedTemplate.env_parameters?.some(
      (field) => field.env_var === 'NGROK_AUTH_TOKEN',
    );
    if (!hasNgrokField) return;

    if (isLocalProvider) {
      // Remove NGROK_AUTH_TOKEN entirely for local/direct sessions
      setConfigFieldValues((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, 'NGROK_AUTH_TOKEN')) {
          return prev;
        }
        const { NGROK_AUTH_TOKEN: _omit, ...rest } = prev;
        return rest;
      });
    } else if (selectedProvider?.type !== 'local') {
      // Ensure remote sessions have a default secret placeholder if none is set
      setConfigFieldValues((prev) => {
        const current = prev['NGROK_AUTH_TOKEN'];
        if (current && current.trim().length > 0) {
          return prev;
        }
        return {
          ...prev,
          NGROK_AUTH_TOKEN: '{{secret._NGROK_AUTH_TOKEN}}',
        };
      });
    }
  }, [isLocalProvider, selectedProvider, selectedTemplate]);

  const handleBack = () => {
    if (isSubmitting) {
      return;
    }
    if (step === 'config') {
      setStep('gallery');
      setSelectedTemplate(null);
      setConfigFieldValues({});
      setModelRegistryToggles({});
    } else if (step === 'gallery') {
      setStep('provider');
    }
  };

  const handleConfigFieldChange = (envVar: string, value: string) => {
    setConfigFieldValues((prev) => ({
      ...prev,
      [envVar]: value,
    }));
  };

  const handleModelRegistryToggle = (envVar: string, checked: boolean) => {
    setModelRegistryToggles((prev) => ({ ...prev, [envVar]: checked }));
    setConfigFieldValues((prev) => {
      const next = { ...prev, [envVar]: '' };
      if (checked) {
        next['MODEL_REGISTRY_TLAB'] = '1';
      } else {
        delete next['MODEL_REGISTRY_TLAB'];
      }
      return next;
    });
  };

  const handleImportTeamTask = async (galleryIdentifier: string | number) => {
    if (isSubmitting) {
      return;
    }
    if (!experimentInfo?.id) {
      addNotification({
        type: 'warning',
        message: 'Please select an experiment first.',
      });
      return;
    }

    setImportingTeamTaskId(galleryIdentifier);
    try {
      const response = await chatAPI.authenticatedFetch(
        chatAPI.Endpoints.Task.ImportFromTeamGallery(experimentInfo.id),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            gallery_id: galleryIdentifier.toString(),
            experiment_id: experimentInfo.id,
            is_interactive: true,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        addNotification({
          type: 'danger',
          message: `Failed to import task: ${errorText}`,
        });
        return;
      }

      const result = await response.json();
      addNotification({
        type: 'success',
        message: result.message || 'Task imported successfully!',
      });

      // Refresh the imported tasks list
      onRefreshTasks();
    } catch (err: any) {
      console.error('Error importing team task:', err);
      addNotification({
        type: 'danger',
        message: `Failed to import task: ${err?.message || String(err)}`,
      });
    } finally {
      setImportingTeamTaskId(null);
    }
  };

  const handleSubmit = (e: React.FormEvent, shouldLaunch: boolean = false) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }
    if (!title.trim()) {
      return;
    }

    if (!selectedProviderId) {
      return;
    }

    if (!selectedTemplate) {
      return;
    }

    // Validate required config fields
    const requiredFields = (
      selectedTemplate.env_parameters?.filter((f) => f.required) || []
    ).filter((f) => !(isLocalProvider && f.env_var === 'NGROK_AUTH_TOKEN'));
    for (const field of requiredFields) {
      if (!configFieldValues[field.env_var]?.trim()) {
        return;
      }
    }

    // Persist model name to history before submitting
    const modelName = configFieldValues['MODEL_NAME'];
    if (modelName?.trim() && selectedTemplate) {
      saveModelToHistory(
        getModelHistoryKey(selectedTemplate.interactive_type),
        modelName.trim(),
      );
    }

    onSubmit(
      {
        title: title.trim(),
        cpus: cpus || undefined,
        memory: memory || undefined,
        accelerators: accelerators || undefined,
        interactive_type: selectedTemplate.interactive_type as
          'vscode' | 'jupyter' | 'vllm' | 'ssh' | 'ollama',
        template_id: selectedTemplate.id,
        provider_id: selectedProviderId,
        env_parameters: configFieldValues,
        local: !!isLocalProvider,
      },
      shouldLaunch,
    );
  };

  const canSubmit = React.useMemo(() => {
    if (!title.trim() || !selectedProviderId || !selectedTemplate) {
      return false;
    }

    const requiredFields = (
      selectedTemplate.env_parameters?.filter((f) => f.required) || []
    ).filter((f) => !(isLocalProvider && f.env_var === 'NGROK_AUTH_TOKEN'));
    for (const field of requiredFields) {
      if (!configFieldValues[field.env_var]?.trim()) {
        return false;
      }
    }

    return true;
  }, [
    title,
    selectedProviderId,
    selectedTemplate,
    configFieldValues,
    isLocalProvider,
  ]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          maxHeight: '80vh',
          width:
            step === 'provider' ? '40vw' : step === 'gallery' ? '70vw' : '60vw',
          overflow: 'hidden',
        }}
      >
        <ModalClose />
        <DialogTitle>
          {step === 'provider'
            ? 'Select Compute Provider'
            : step === 'gallery'
              ? 'New Interactive Task'
              : 'Configure Task'}
        </DialogTitle>
        <form onSubmit={(e) => handleSubmit(e, false)}>
          <DialogContent
            sx={{ maxHeight: '60vh', overflow: 'auto', padding: 1 }}
          >
            {submitError && (
              <Alert
                variant="soft"
                color="danger"
                sx={{ mb: 1 }}
                endDecorator={
                  onClearSubmitError ? (
                    <IconButton
                      variant="plain"
                      color="danger"
                      onClick={onClearSubmitError}
                      aria-label="Dismiss error"
                    >
                      <XIcon size={16} />
                    </IconButton>
                  ) : null
                }
              >
                <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                  {submitError}
                </Typography>
              </Alert>
            )}
            {step === 'provider' && (
              <Stack spacing={3} sx={{ py: 2 }}>
                <Typography level="body-md">
                  First, select the compute provider you want to use for this
                  interactive session. We'll show you compatible tasks in the
                  next step.
                </Typography>
                <FormControl required>
                  <FormLabel>Compute Provider</FormLabel>
                  <Select
                    placeholder={
                      providers.length
                        ? 'Select a compute provider'
                        : 'No compute providers configured'
                    }
                    value={selectedProviderId || null}
                    onChange={(_, value) => setSelectedProviderId(value || '')}
                    disabled={
                      isSubmitting ||
                      isProvidersLoading ||
                      providers.length === 0
                    }
                    slotProps={{
                      listbox: { sx: { maxHeight: 240 } },
                    }}
                  >
                    {providers.map((provider) => (
                      <Option key={provider.id} value={provider.id}>
                        {provider.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>

                {selectedProvider &&
                  selectedProvider.config?.supported_accelerators &&
                  selectedProvider.config.supported_accelerators.length > 0 && (
                    <Box>
                      <Typography level="body-xs" sx={{ mb: 1 }}>
                        Supported Accelerators:
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {selectedProvider.config.supported_accelerators.map(
                          (acc: string) => (
                            <Chip
                              key={acc}
                              size="sm"
                              variant="soft"
                              color="primary"
                            >
                              {acc}
                            </Chip>
                          ),
                        )}
                      </Stack>
                    </Box>
                  )}

                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                >
                  <Button
                    disabled={!selectedProviderId || isSubmitting}
                    onClick={() => setStep('gallery')}
                    endDecorator={<ArrowRightIcon size={16} />}
                  >
                    Next: Choose Task
                  </Button>
                </Box>
              </Stack>
            )}
            {step === 'config' && (
              <Stack spacing={3}>
                <FormControl required>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Interactive session name"
                    autoFocus
                    disabled={isSubmitting}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Compute Provider</FormLabel>
                  <Input
                    value={
                      selectedProvider?.name ||
                      (providers.length
                        ? 'Provider selected in previous step'
                        : 'No providers configured')
                    }
                    disabled
                    readOnly
                  />
                  <FormHelperText>
                    Provider was chosen on the previous screen.
                  </FormHelperText>
                </FormControl>

                {selectedTemplate?.interactive_type === 'ssh' && (
                  <Alert color="warning" variant="soft">
                    <Typography
                      level="body-sm"
                      fontWeight="bold"
                      sx={{ mb: 0.5 }}
                    >
                      Security Warning
                    </Typography>
                    <Typography level="body-xs">
                      This will create a public TCP tunnel. Be careful when
                      sharing the SSH command with anyone, as it provides direct
                      access to your remote machine.
                    </Typography>
                  </Alert>
                )}

                {selectedTemplate?.env_parameters &&
                  selectedTemplate.env_parameters.length > 0 && (
                    <>
                      {selectedTemplate.env_parameters
                        .filter((field) => field.env_var !== 'NGROK_AUTH_TOKEN')
                        .map((field) => (
                          <FormControl
                            key={field.env_var}
                            required={
                              field.required &&
                              !(
                                isLocalProvider &&
                                field.env_var === 'NGROK_AUTH_TOKEN'
                              )
                            }
                            disabled={
                              isSubmitting ||
                              (isLocalProvider &&
                                field.env_var === 'NGROK_AUTH_TOKEN')
                            }
                          >
                            <FormLabel>{field.field_name}</FormLabel>
                            {field.field_type === 'model' ? (
                              <Stack spacing={1}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Switch
                                    size="sm"
                                    checked={
                                      !!modelRegistryToggles[field.env_var]
                                    }
                                    onChange={(e) =>
                                      handleModelRegistryToggle(
                                        field.env_var,
                                        e.target.checked,
                                      )
                                    }
                                    disabled={isSubmitting}
                                    slotProps={{
                                      input: {
                                        'aria-label':
                                          'Load from TLab model registry',
                                      },
                                    }}
                                  />
                                  <Typography level="body-sm">
                                    Load from TLab model registry
                                  </Typography>
                                </Stack>
                                {modelRegistryToggles[field.env_var] ? (
                                  <Select
                                    placeholder="Select a model from registry"
                                    value={
                                      configFieldValues[field.env_var] || null
                                    }
                                    onChange={(_, value) =>
                                      handleConfigFieldChange(
                                        field.env_var,
                                        value || '',
                                      )
                                    }
                                    disabled={isSubmitting}
                                    slotProps={{
                                      listbox: { sx: { maxHeight: 240 } },
                                    }}
                                  >
                                    {(registryModels.length > 0
                                      ? registryModels
                                      : installedModels
                                    ).map((m) => (
                                      <Option
                                        key={m.model_id}
                                        value={m.model_id}
                                      >
                                        {m.name || m.model_id}
                                      </Option>
                                    ))}
                                  </Select>
                                ) : (
                                  <ModelNameInput
                                    value={
                                      configFieldValues[field.env_var] || ''
                                    }
                                    onChange={(v) =>
                                      handleConfigFieldChange(field.env_var, v)
                                    }
                                    taskTypeOrId={
                                      selectedTemplate?.interactive_type
                                    }
                                    placeholder={field.placeholder}
                                    disabled={isSubmitting}
                                    required={!!field.required}
                                  />
                                )}
                              </Stack>
                            ) : (
                              <Input
                                type={
                                  field.password
                                    ? 'password'
                                    : field.field_type === 'integer'
                                      ? 'number'
                                      : 'text'
                                }
                                value={configFieldValues[field.env_var] || ''}
                                onChange={(e) =>
                                  handleConfigFieldChange(
                                    field.env_var,
                                    e.target.value,
                                  )
                                }
                                placeholder={field.placeholder}
                                disabled={
                                  isSubmitting ||
                                  (isLocalProvider &&
                                    field.env_var === 'NGROK_AUTH_TOKEN')
                                }
                              />
                            )}
                            {field.help_text && (
                              <FormHelperText>{field.help_text}</FormHelperText>
                            )}
                          </FormControl>
                        ))}
                    </>
                  )}

                {selectedProvider?.type !== 'local' && (
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ flexWrap: 'wrap', rowGap: 2 }}
                  >
                    <FormControl sx={{ minWidth: '160px', flex: 1 }}>
                      <FormLabel>CPUs</FormLabel>
                      <Input
                        value={cpus}
                        onChange={(e) => setCpus(e.target.value)}
                        placeholder="e.g. 4"
                        disabled={isSubmitting}
                      />
                    </FormControl>

                    <FormControl sx={{ minWidth: '160px', flex: 1 }}>
                      <FormLabel>Memory (GB)</FormLabel>
                      <Input
                        value={memory}
                        onChange={(e) => setMemory(e.target.value)}
                        placeholder="e.g. 16"
                        disabled={isSubmitting}
                      />
                    </FormControl>

                    <FormControl sx={{ minWidth: '200px', flex: 2 }}>
                      <FormLabel>Accelerators</FormLabel>
                      <Input
                        value={accelerators}
                        onChange={(e) => setAccelerators(e.target.value)}
                        placeholder="e.g. RTX3090:1 or H100:8"
                        disabled={isSubmitting}
                      />
                    </FormControl>
                  </Stack>
                )}

                <FormHelperText>
                  Setup and command are pre-populated based on the selected
                  interactive type.
                </FormHelperText>
              </Stack>
            )}
            {step === 'gallery' && (
              <Stack spacing={3}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography level="title-md">
                    Choose an interactive source
                  </Typography>
                </Box>
                <Tabs
                  size="sm"
                  value={activeGalleryTab}
                  onChange={(_e, val) => {
                    if (isSubmitting || !val) {
                      return;
                    }
                    setActiveGalleryTab(
                      val as 'interactive' | 'team-interactive',
                    );
                  }}
                >
                  <TabList>
                    <Tab value="interactive">Interactive Gallery</Tab>
                    <Tab value="team-interactive">Team Interactive Gallery</Tab>
                  </TabList>
                </Tabs>

                {activeGalleryTab === 'interactive' && (
                  <>
                    <Stack spacing={1}>
                      {importedTasks.length === 0 ? (
                        <>
                          <Typography level="title-md">
                            Get Started with Interactive Tasks
                          </Typography>
                          <Typography level="body-sm" color="neutral">
                            Select an interactive task type to configure. Start
                            with VS Code, Jupyter, or vLLM.
                          </Typography>
                        </>
                      ) : (
                        <Typography level="body-sm" color="neutral">
                          Pick an interactive template from the gallery to
                          configure a new task.
                        </Typography>
                      )}
                    </Stack>

                    {(galleryIsLoading || !galleryData) && (
                      <Grid container spacing={2}>
                        {[1, 2, 3].map((i) => (
                          <Grid xs={12} sm={6} md={4} key={i}>
                            <Card variant="outlined">
                              <CardContent>
                                <Skeleton
                                  variant="rectangular"
                                  width={32}
                                  height={28}
                                />
                                <Skeleton
                                  variant="text"
                                  level="title-md"
                                  sx={{ mt: 1 }}
                                />
                                <Skeleton
                                  variant="text"
                                  level="body-sm"
                                  sx={{ mt: 1 }}
                                />
                                <Skeleton
                                  variant="text"
                                  level="body-sm"
                                  width="60%"
                                />
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                    {!galleryIsLoading &&
                      galleryData &&
                      gallery.length === 0 && (
                        <Typography level="body-sm" color="danger">
                          No interactive task templates available.
                        </Typography>
                      )}
                    {!galleryIsLoading && galleryData && gallery.length > 0 && (
                      <Grid container spacing={2}>
                        {gallery
                          .filter((template) => {
                            if (
                              selectedProvider?.type === 'local' &&
                              (template as InteractiveTemplate).remoteOnly
                            ) {
                              return false;
                            }
                            return isProviderCompatible(
                              selectedProvider,
                              template.supported_accelerators,
                            );
                          })
                          .map((template) => (
                            <Grid xs={12} sm={6} md={4} key={template.id}>
                              <Card
                                variant="outlined"
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    boxShadow: 'md',
                                    borderColor: 'primary.500',
                                  },
                                }}
                                onClick={() => {
                                  if (!isSubmitting) {
                                    handleTemplateSelect(template);
                                  }
                                }}
                              >
                                <CardContent>
                                  {template.icon && (
                                    <img
                                      src={template.icon}
                                      alt={`${template.name} icon`}
                                      width={32}
                                      height={32}
                                    />
                                  )}
                                  <Typography level="title-md">
                                    {template.name}
                                  </Typography>
                                  <Typography level="body-sm" sx={{ mt: 1 }}>
                                    {template.description}
                                  </Typography>
                                  {template.supported_accelerators && (
                                    <Box sx={{ mt: 1.5 }}>
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        flexWrap="wrap"
                                      >
                                        {(Array.isArray(
                                          template.supported_accelerators,
                                        )
                                          ? template.supported_accelerators
                                          : [template.supported_accelerators]
                                        ).map((acc: string) => (
                                          <Chip
                                            key={acc}
                                            size="sm"
                                            variant="soft"
                                            color="primary"
                                          >
                                            {acc}
                                          </Chip>
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                      </Grid>
                    )}
                  </>
                )}

                {activeGalleryTab === 'team-interactive' && (
                  <>
                    <Stack spacing={1}>
                      <Typography level="title-md">
                        Team Interactive Gallery
                      </Typography>
                      <Typography level="body-sm" color="neutral">
                        Import interactive task templates shared by your team.
                      </Typography>
                    </Stack>

                    {teamGalleryIsLoading && (
                      <Grid container spacing={2}>
                        {[1, 2, 3].map((i) => (
                          <Grid xs={12} sm={6} md={4} key={i}>
                            <Card variant="outlined">
                              <CardContent>
                                <Skeleton
                                  variant="rectangular"
                                  width={32}
                                  height={28}
                                />
                                <Skeleton
                                  variant="text"
                                  level="title-md"
                                  sx={{ mt: 1 }}
                                />
                                <Skeleton
                                  variant="text"
                                  level="body-sm"
                                  sx={{ mt: 1 }}
                                />
                                <Skeleton
                                  variant="text"
                                  level="body-sm"
                                  width="60%"
                                />
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {!teamGalleryIsLoading &&
                      teamGalleryData &&
                      teamInteractiveGallery.length === 0 && (
                        <Typography level="body-sm" color="neutral">
                          No team interactive tasks available.
                        </Typography>
                      )}

                    {!teamGalleryIsLoading &&
                      teamGalleryData &&
                      teamInteractiveGallery.length > 0 && (
                        <Grid container spacing={2}>
                          {teamInteractiveGallery
                            .filter((task: any) => {
                              if (
                                selectedProvider?.type === 'local' &&
                                task.remoteOnly
                              ) {
                                return false;
                              }
                              return isProviderCompatible(
                                selectedProvider,
                                task.supported_accelerators ||
                                  task.config?.supported_accelerators,
                              );
                            })
                            .map((task: any, index: number) => {
                              const taskTitle =
                                task.title || task.name || 'Untitled Task';
                              const taskId =
                                task?.id ||
                                task?.name ||
                                task?.title ||
                                index.toString();
                              let galleryIdentifier: string | number;
                              if (task?.id) {
                                galleryIdentifier = task.id;
                              } else if (task?.name) {
                                galleryIdentifier = task.name;
                              } else if (task?.title) {
                                galleryIdentifier = task.title;
                              } else {
                                galleryIdentifier = index;
                              }

                              const taskSupportedAccelerators =
                                task.supported_accelerators ||
                                task.config?.supported_accelerators;

                              return (
                                <Grid xs={12} sm={6} md={4} key={taskId}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      cursor: 'pointer',
                                      '&:hover': {
                                        boxShadow: 'md',
                                        borderColor: 'primary.500',
                                      },
                                    }}
                                    onClick={() => {
                                      if (!isSubmitting) {
                                        handleImportTeamTask(galleryIdentifier);
                                      }
                                    }}
                                  >
                                    <CardContent>
                                      {task.icon && (
                                        <img
                                          src={task.icon}
                                          alt={`${taskTitle} icon`}
                                          width={32}
                                          height={32}
                                        />
                                      )}
                                      <Typography level="title-md">
                                        {taskTitle}
                                      </Typography>
                                      <Typography
                                        level="body-sm"
                                        sx={{ mt: 1 }}
                                      >
                                        {task.description || 'No description'}
                                      </Typography>
                                      {taskSupportedAccelerators && (
                                        <Box sx={{ mt: 1.5 }}>
                                          <Stack
                                            direction="row"
                                            spacing={0.5}
                                            flexWrap="wrap"
                                          >
                                            {(Array.isArray(
                                              taskSupportedAccelerators,
                                            )
                                              ? taskSupportedAccelerators
                                              : [taskSupportedAccelerators]
                                            ).map((acc: string) => (
                                              <Chip
                                                key={acc}
                                                size="sm"
                                                variant="soft"
                                                color="primary"
                                              >
                                                {acc}
                                              </Chip>
                                            ))}
                                          </Stack>
                                        </Box>
                                      )}
                                      {importingTeamTaskId ===
                                        galleryIdentifier && (
                                        <Typography
                                          level="body-xs"
                                          color="primary"
                                          sx={{ mt: 1 }}
                                        >
                                          Importing…
                                        </Typography>
                                      )}
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                        </Grid>
                      )}
                  </>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Stack
              direction="row"
              spacing={2}
              sx={{ width: '100%', justifyContent: 'space-between' }}
            >
              {step !== 'provider' && (
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={handleBack}
                  disabled={isSubmitting}
                  startDecorator={<ArrowLeftIcon size={16} />}
                >
                  Back
                </Button>
              )}
              {step === 'config' && (
                <Stack direction="row" spacing={1} alignItems="center">
                  {isSubmitting && (
                    <Typography level="body-sm">
                      {loadingMessages[loadingMessageIndex]}
                    </Typography>
                  )}
                  <Button
                    variant="solid"
                    color="primary"
                    loading={isSubmitting}
                    disabled={isSubmitting || !canSubmit}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSubmit(e, true);
                    }}
                    endDecorator={<ArrowRightIcon size={16} />}
                  >
                    Launch
                  </Button>
                </Stack>
              )}
            </Stack>
          </DialogActions>
        </form>
      </ModalDialog>
    </Modal>
  );
}
